import {
  ActionMenu,
  Button,
  Field,
  H4,
  IconButton,
  Input,
  LabelledCheckbox,
  Select,
  SkeletonLoader,
  Stack,
  useActions
} from "@introist/react-foundation/v2";
import { HtmlEditor } from "components/editors/v4/HtmlEditor/HtmlEditor";
import {
  FormField,
  FormFieldCreate,
  FormFieldOptions,
  FormFieldUpdate,
  useFormField,
  useFormFieldActions
} from "../../useFormFields";
import { useFormPages } from "../../useFormPages";

import { UnsavedChanges } from "../../../../components/molecules";
import { useObjectForm } from "./useObjectForm";
import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useEmployeeFields } from "modules/employees/hooks/useEmployeeFields";
import { StepEditorAccordion } from "../../../workflows/routes/WorkflowEditor/StepEditorAccordion";

export const FormFieldEditor = ({
  formId,
  fieldId,
  onClose
}: {
  formId: string;
  fieldId: string;
  onClose: () => unknown;
}) => {
  const { fields } = useEmployeeFields();
  const { formPages } = useFormPages(formId);
  const [searchParams] = useSearchParams();

  const employeeFieldOptions = useMemo(() => {
    if (!fields || fields.length === 0) return [];
    return fields.map(field => ({ key: field.key, title: field.title }));
  }, [fields]);

  const { onConfirmAction } = useActions();

  const { field, loading } = useFormField(fieldId, { enabled: fieldId && fieldId !== "new" });
  const { create, update, archive } = useFormFieldActions();

  const { value, onChange, hasChanges, submit, errors } = useObjectForm<FormField>(
    field,
    fieldId === "new" ? { type: "text", pageId: searchParams.get("pageId") } : {}
  );

  const onSave = submit(async changes => {
    if (fieldId === "new") {
      return create({ ...changes, formId } as FormFieldCreate).then(onClose);
    } else {
      return update({ id: fieldId!, update: changes as FormFieldUpdate["update"] });
    }
  });

  const onArchive = onConfirmAction(
    async () => {
      await archive(fieldId!).then(onClose);
    },
    {
      title: "Confirm archive field",
      description: "Are you sure to archive this field?"
    }
  );

  const onTypeChange = (newType: string) => {
    onChange("type")(newType);
    if (newType !== "select" && newType !== "scale") {
      onChange("options")(null);
    }
    if (newType !== "select") {
      onChange("display")(null);
    }
  };

  return (
    <Stack vertical style={{ padding: "var(--spacing-xLarge)" }}>
      <Stack>
        <H4>{fieldId === "new" ? "New Form Field" : field?.title}</H4>
        <Stack gap="small" style={{ marginLeft: "auto" }}>
          {fieldId !== "new" && (
            <ActionMenu
              options={[
                {
                  key: "archive",
                  title: "Archive field",
                  startAdornmentIcon: "archive",
                  onClick: onArchive
                }
              ]}
            />
          )}
          <IconButton style={{ marginLeft: "auto" }} icon="crossSmall" onClick={onClose} />
        </Stack>
      </Stack>

      <Field title="Title" error={errors.title}>
        {loading ? (
          <SkeletonLoader height={36} />
        ) : (
          <Input
            value={value.title ?? ""}
            onChange={onChange("title")}
            autoFocus={fieldId === "new"}
            error={!!errors.title}
          />
        )}
      </Field>
      <Field title="Short Title (optional)">
        {loading ? (
          <SkeletonLoader height={28} />
        ) : (
          <Input
            size="small"
            placeholder="Short tag for internal use"
            value={value.shortTitle ?? ""}
            onChange={newValue => onChange("shortTitle")(newValue || null)}
            error={!!errors.shortTitle}
          />
        )}
      </Field>
      <Field title="Description" error={errors.description}>
        {loading ? (
          <SkeletonLoader height={120} />
        ) : (
          <HtmlEditor
            content={value.description ?? ""}
            onChange={onChange("description")}
            placeholder="Extra information for the submitter"
            error={!!errors.description}
            underline={true}
            fields={false}
            emojis={true}
            images={true}
            gifs={true}
          />
        )}
      </Field>
      <StepEditorAccordion title="Type">
        <Stack vertical>
          <Select
            size="small"
            style={{ width: "100%" }}
            options={FormFieldOptions}
            value={value.type}
            onSelect={opt => onTypeChange(opt.key)}
            error={!!errors.type}
            attachToRef={false}
          />
          {value.type === "select" && (
            <Field title="Display style">
              <Select
                size="small"
                style={{ width: "100%" }}
                options={[
                  { key: "dropdown", title: "Dropdown" },
                  { key: "radio", title: "Radio buttons" }
                ]}
                value={value.display ?? "dropdown"}
                onSelect={opt => onChange("display")(opt.key)}
                attachToRef={false}
              />
            </Field>
          )}
          <LabelledCheckbox
            label="This field is required"
            checked={value.required ?? false}
            onChange={onChange("required")}
            style={{ margin: "var(--spacing-medium) 0" }}
          />
          <Field title="Prefill" error={errors.prefill}>
            <Select
              size="small"
              style={{ width: "100%" }}
              options={employeeFieldOptions}
              value={value.prefill ?? undefined}
              onSelect={opt => onChange("prefill")(opt.key)}
              onClear={() => onChange("prefill")(null)}
              error={!!errors.prefill}
              searchable
              attachToRef={false}
            />
          </Field>
          {formPages && formPages.length > 0 && (
            <Field title="Page">
              <Select
                size="small"
                style={{ width: "100%" }}
                options={formPages.map(page => ({ key: page.id, title: page.title }))}
                value={
                  formPages.some(page => page.id === value.pageId) ? value.pageId! : formPages[0].id
                }
                onSelect={opt => onChange("pageId")(opt.key)}
                attachToRef={false}
              />
            </Field>
          )}
        </Stack>
      </StepEditorAccordion>

      {(value.type === "select" || value.type === "scale") && (
        <>
          {loading ? (
            <Field title="Options">
              <SkeletonLoader height={28} />
            </Field>
          ) : (
            <StepEditorAccordion title="Options">
              <OptionField
                value={value.options ?? []}
                onChange={onChange("options")}
                error={errors.options}
                required={value.type === "select"}
              />
            </StepEditorAccordion>
          )}
        </>
      )}
      <UnsavedChanges
        show={hasChanges}
        onSave={onSave}
        style={{
          position: "sticky",
          bottom: "1rem",
          width: "250px",
          left: "calc(50% - 125px)"
        }}
      />
    </Stack>
  );
};

const OptionField = ({
  value,
  onChange,
  error,
  required = false
}: {
  value: string[];
  onChange: (options: string[]) => unknown;
  error?: string;
  required?: boolean;
}) => (
  <Field title="" error={error} required={required}>
    {value.map((option, index) => (
      <Stack key={index}>
        <Input
          size="small"
          value={option}
          onChange={newVal => {
            const newOptions = value.map((o, i) => (index === i ? newVal : o));
            onChange(newOptions);
          }}
          style={{ flex: 1 }}
          placeholder="Option value"
        />
        <IconButton
          icon="crossSmall"
          onClick={() => {
            onChange(value.filter(o => o !== option));
          }}
        />
      </Stack>
    ))}

    <Button variant="blended" startIcon="plus" onClick={() => onChange([...value, ""])}>
      Add option
    </Button>
  </Field>
);
